import type {QueryClient} from "@tanstack/react-query";
import {DateTime} from "luxon";
import {fetchAnalyticsData, getDataset} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-api";
import {DASHBOARD_DATASET_KEYS_BY_WORKSPACE} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-constants";
import {
    EMOTORAD_DASHBOARD_DISTANCE_BAR_COLOR,
    EMOTORAD_DASHBOARD_DONUT_SLICE_COLORS,
    EMOTORAD_DASHBOARD_FUNNEL_BASE_COLOR,
    EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-emotoradDashboardChartColors";
import {resolveDashboardDatasetsAndColumns} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-helpers";
import {DashboardRowType, type DashboardConfig} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/dashboard/-types";
import {DatasetColumnDataType, DatasetColumnRole, FilterType, MetricAggregationFunction, type Dataset, type Filter} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import {AnalyticsQueryVariant, type DataResolverProps, type RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import {NumberSystems} from "~/utilities/typeDefinitions";
import type {GraphBaseColor} from "~/utilities/analytics/graphColors";
import {WidgetType, type Widget} from "~/components/analyticsWidgets/widgetTypes/widget";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uniqueUuid} from "@vesta/unique/unique";
import {getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {EmotoradRoles, EMOTORAD__USER__DEALERSHIP__COLUMN_ID} from "~/domainModels/workspaceSpecific/emotorad";
import type {OrganizationalUnit} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {extractEmotoradDealershipOuIdsFromUserValue} from "~/utilities/emotoradDealershipSubmitResolution";

/**
 * Synthetic (frontend-only) metric column id for the "Zero-Sales High Leads %" tile.
 * The value is computed from `Leads` and `Sales` in the widget's `dataResolver`; it is never sent to the backend.
 */
const EMOTORAD__COMPUTED__ZERO_SALES_HIGH_LEADS_PERCENT__COLUMN_ID = uniqueUuid`9c1d4a55-0001-4d11-9001-0000fe770001`;

/**
 * Synthetic (frontend-only) metric column id for the "No Call Activity (SSE)" tile.
 * The value is computed by counting SSE users with zero calls in the period.
 */
const EMOTORAD__COMPUTED__NO_CALL_ACTIVITY_SSE__COLUMN_ID = uniqueUuid`9c1d4a55-0002-4d11-9001-0000fe770002`;

/**
 * Emotorad heat map should stay fixed to Zone-level OUs irrespective of global dashboard filters.
 * The periodic-as-periodic endpoint does not support the `ouTemplateId` source-level filter, so
 * the heatmap's dataResolver post-filters to this template level using the organizational units list.
 */
const EMOTORAD__HEATMAP__ZONE_OU_TEMPLATE_ID = uniqueUuid`12a598fd-dcb8-4e7a-88f9-978b738cfb7d`;

function heatMapCanonicalDayIso(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
        return "";
    }
    const s = typeof value === "string" ? value : String(value);
    const parsed = DateTime.fromISO(s, {setZone: true});
    if (parsed.isValid !== true) {
        return s;
    }
    return parsed.startOf("day").toISO()!;
}

/**
 * One ISO instant per calendar day in the month containing the instant just before {@link toExclusive}.
 * Dashboard fetch uses an exclusive `to` upper bound; the last included civil day lives in that month.
 */
function heatMapFixedColumnKeysForCalendarMonthOfRangeEnd(toExclusive: Iso8601DateTime): ReadonlyArray<string> {
    const end = DateTime.fromISO(toExclusive, {setZone: true});
    if (end.isValid !== true) {
        return [];
    }
    const lastIncluded = end.minus({milliseconds: 1});
    const monthStart = lastIncluded.startOf("month").startOf("day");
    const monthEnd = lastIncluded.endOf("month").startOf("day");
    const out: string[] = [];
    for (let d = monthStart; d <= monthEnd; d = d.plus({days: 1})) {
        out.push(d.toISO()!);
    }
    return out;
}

export function getEmotoradDashboardConfig({
    datasets,
    intellsysBaseUrl,
    accessToken,
    workspaceId,
    queryClient,
    users,
    userTemplates,
    platformUserDetailsByUserId,
    organizationalUnits,
    ouTemplates,
    timeZone,
    startTime,
    endTime,
}: {
    datasets: Array<Dataset>;
    intellsysBaseUrl: string;
    accessToken: string;
    workspaceId: Uuid;
    queryClient: QueryClient;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    platformUserDetailsByUserId: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    /**
     * Used to resolve linked dealership OU ids on users (e.g. Dealership owner row) into the
     * same display names used elsewhere (OU instance template).
     */
    organizationalUnits: Array<OrganizationalUnit>;
    /**
     * Used by `fetchAnalyticsData → transformDataIfNeeded` to resolve OU dimension UUIDs into
     * human-readable display names. Required for any widget that consumes the OU dimension
     * (e.g. the OU × Date heat map) — without it, widgets surface raw UUIDs as row / slice labels.
     */
    ouTemplates: Array<OrganizationalUnitTemplate>;
    timeZone: Uuid;
    startTime: Iso8601DateTime;
    endTime: Iso8601DateTime;
}): DashboardConfig {
    const dashboardData = resolveDashboardDatasetsAndColumns(
        datasets,
        DASHBOARD_DATASET_KEYS_BY_WORKSPACE.emotorad,
    );
    const emotoradPerformanceDataset = dashboardData.datasets.emotoradPerformanceDataset;
    const emotoradDailyPerformanceDataset = dashboardData.datasets.emotoradDailyPerformanceDataset;
    const EMOTORAD = dashboardData.columnIds.emotoradPerformanceDataset;
    const EMOTORAD_DAILY = dashboardData.columnIds.emotoradDailyPerformanceDataset;

    const partialGetDataset = (datasetId: Uuid) =>
        getDataset(
            datasetId,
            intellsysBaseUrl,
            accessToken,
            workspaceId,
        );
    const partialFetchData = (dataset: Dataset, props: DataResolverProps) =>
        fetchAnalyticsData(
            intellsysBaseUrl,
            accessToken,
            workspaceId,
            queryClient,
            // Forwarded so `transformDataIfNeeded` can swap user / OU UUIDs for display names
            // on returned dimension columns (see helpers.transformColumnValues).
            users,
            userTemplates,
            ouTemplates,
            dashboardData,
            dataset,
            props,
        );

    const makeNumberTileWidget = (
        id: Uuid,
        title: string | null,
        metrics: Uuid[],
        valueSuffix: string = "",
        decimalPlaces: number = 0,
    ): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: emotoradPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix,
    });

    const makeChartWidget = (
        id: Uuid,
        type: WidgetType.pieChart
            | WidgetType.donutChart
            | WidgetType.multiBarGraph
            | WidgetType.stackedBarGraph
            | WidgetType.horizontalBarGraph
            | WidgetType.lineGraph
            | WidgetType.areaGraph
            | WidgetType.comboChart
            | WidgetType.table,
        title: string | null,
        metrics: Uuid[],
        dimensions: Uuid[],
        queryVariant: AnalyticsQueryVariant = AnalyticsQueryVariant.periodicAsNonTimeSeries,
        options?: {
            dismissChartHoverTooltipOnPointerLeave?: boolean;
            chartColorPalette?: ReadonlyArray<string>;
        },
    ): Widget => ({
        id,
        type,
        datasetId: emotoradPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions,
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant,
        dismissChartHoverTooltipOnPointerLeave: options?.dismissChartHoverTooltipOnPointerLeave,
        chartColorPalette: options?.chartColorPalette,
    });

    /**
     * Indexed lookups used by the leader-table row-path resolver. Computed once per
     * dashboard config build so we don't pay an O(N) `users.find` cost per leaf row.
     */
    const userByUserId = new Map<Uuid, User>();
    for (const user of users) {
        userByUserId.set(user.userId, user);
    }

    /**
     * For a given leaf user, walks `parentId` upward and returns the chain (leaf-first).
     * Defensive against cycles via a visited set — workspace data is user-edited and
     * malformed parent chains have shipped before.
     */
    const getParentChainLeafFirst = (leafUserId: Uuid): Array<User> => {
        const chain: Array<User> = [];
        const seen = new Set<Uuid>();
        let current: User | undefined = userByUserId.get(leafUserId);
        while (current !== undefined && seen.has(current.id) === false) {
            seen.add(current.id);
            chain.push(current);
            if (current.parentId === null) {
                break;
            }
            current = userByUserId.get(current.parentId);
        }
        return chain;
    };

    const dealershipOuById = new Map<string, OrganizationalUnit>();
    for (const ou of organizationalUnits) {
        dealershipOuById.set(String(ou.id).toLowerCase(), ou);
    }

    /** Dealership owner column should show linked showroom OU label, not the person's name. */
    const resolveLinkedDealership = (user: User): {id: string; label: string} => {
        const raw = user.additionalColumnValues[EMOTORAD__USER__DEALERSHIP__COLUMN_ID];
        const ouIds = extractEmotoradDealershipOuIdsFromUserValue(raw);
        if (ouIds.length === 0) {
            return {id: "dealer-none", label: "-"};
        }
        const parts: string[] = [];
        for (const id of ouIds) {
            const ou = dealershipOuById.get(id.toLowerCase());
            if (ou === undefined) {
                continue;
            }
            const tmpl = ouTemplates.find((t) => t.id === ou.templateId);
            const label = tmpl !== undefined
                ? processOttopilotStringTemplate(tmpl.instanceDisplayNameTemplate, ou.additionalColumnValues).trim()
                : "";
            parts.push(label !== "" ? label : id);
        }
        return {
            // Join OU ids so two dealers with different showrooms get distinct group keys.
            id: ouIds.join(""),
            label: parts.length > 0 ? parts.join(", ") : "-",
        };
    };

    /**
     * Builds a `resolveRowPath(leafUserId)` function for the given role chain.
     * For each role-chain entry (top → leaf) we find the first ancestor (or the leaf itself)
     * whose `templateId` matches. Default: user display name; optional `pathDisplay: "dealership"`
     * uses linked dealership OU labels for that role (Emotorad). When an ancestor is missing
     * we emit "-" so the leaf still appears in the table at a partial depth.
     */
    const buildLeaderTableRowPathResolver = (
        roleChainTopDown: Array<{templateId: Uuid; pathDisplay?: "user" | "dealership"}>,
    ): ((leafUserId: Uuid) => Array<{id: string; label: string}> | null) => {
        return (leafUserId: Uuid) => {
            const chainLeafFirst = getParentChainLeafFirst(leafUserId);
            if (chainLeafFirst.length === 0) {
                return null;
            }
            const path: Array<{id: string; label: string}> = [];
            for (const entry of roleChainTopDown) {
                const match = chainLeafFirst.find((u) => u.templateId === entry.templateId);
                if (match === undefined) {
                    // Stable placeholder so all rows missing this level share one group.
                    path.push({id: `missing-${entry.templateId}`, label: "-"});
                    continue;
                }
                if (entry.pathDisplay === "dealership") {
                    path.push(resolveLinkedDealership(match));
                } else {
                    path.push({id: match.userId, label: getUserDisplayName(match.userId, users, userTemplates, platformUserDetailsByUserId)});
                }
            }
            return path;
        };
    };

    /**
     * Leader-table widget — wraps the framework widget with closures over `users` /
     * `userTemplates` (workspace data the framework deliberately doesn't know about).
     *
     * The widget asks the backend for per-user metrics (`dimensions: [user]`) filtered to
     * the leaf role (e.g. SSE) so we get one row per leaf, then the transformer walks each
     * leaf's `parentId` chain to assemble the path used by AG Grid's tree mode.
     */
    const makeLeaderTableWidget = ({
        id,
        title,
        metrics,
        metricColumnLabels,
        roleChainTopDown,
        leafTemplateIdsToInclude,
    }: {
        id: Uuid;
        title: string | null;
        metrics: Uuid[];
        metricColumnLabels: string[];
        /** Role hierarchy levels rendered as columns, top-of-tree → leaf. */
        roleChainTopDown: Array<{templateId: Uuid; columnLabel: string; pathDisplay?: "user" | "dealership"}>;
        /**
         * Templates whose users represent leaf rows in this table — sent as a `stringIn` filter
         * on `userTemplate` so the backend only returns leaf-level metrics. Typically the same
         * single template id as the deepest entry in `roleChainTopDown` (e.g. SSE).
         */
        leafTemplateIdsToInclude: Array<Uuid>;
    }): Widget => ({
        id,
        type: WidgetType.leaderTable,
        datasetId: emotoradPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [EMOTORAD.user],
        metrics,
        externalFilters: [],
        editTimeFilters: [
            {
                type: FilterType.stringIn,
                attribute: EMOTORAD.userTemplate,
                values: leafTemplateIdsToInclude,
            },
        ],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        pathColumnHeaders: roleChainTopDown.map((role) => role.columnLabel),
        metricColumnLabels,
        userDimensionColumnId: EMOTORAD.user,
        resolveRowPath: buildLeaderTableRowPathResolver(roleChainTopDown),
    });

    /**
     * Funnel chart widget — has its own helper because the `Widget` discriminated union
     * carries funnel-only render flags (showValues / showConversionRate / showDropOffRate)
     * that the generic `makeChartWidget` doesn't know about. Stages are emitted in the order
     * of `metrics` here, top-of-funnel first.
     */
    const makeFunnelWidget = ({
        id,
        title,
        metrics,
        showValues = true,
        showConversionRate = false,
        showDropOffRate = false,
        funnelBaseColor = EMOTORAD_DASHBOARD_FUNNEL_BASE_COLOR,
    }: {
        id: Uuid;
        title: string | null;
        metrics: Uuid[];
        showValues?: boolean;
        showConversionRate?: boolean;
        showDropOffRate?: boolean;
        funnelBaseColor?: GraphBaseColor;
    }): Widget => ({
        id,
        type: WidgetType.funnelChart,
        datasetId: emotoradPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        showValues,
        showConversionRate,
        showDropOffRate,
        funnelBaseColor,
    });

    /**
     * Tabbed dual-axis line chart that renders one volume + one conversion-% line per tab.
     * Uses the periodic analytics endpoint directly so a single request returns the full range.
     */
    const makeTabbedTimeSeriesLineChartWidget = ({
        id,
        title,
        metrics,
        metricTabs,
    }: {
        id: Uuid;
        title: string | null;
        metrics: Uuid[];
        metricTabs: Array<{
            tabId: string;
            label: string;
            primaryMetricId: Uuid;
            secondaryMetricId?: Uuid;
            derivedSecondary?: {
                numeratorMetricId: Uuid;
                denominatorMetricId: Uuid;
                multiplyBy: number;
                label: string;
            };
            seriesColors?: {
                primary: string;
                secondary: string;
            };
        }>;
    }): Widget => ({
        id,
        type: WidgetType.tabbedTimeSeriesLineChart,
        datasetId: emotoradDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics,
        metricTabs,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: partialFetchData,
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
        dismissChartHoverTooltipOnPointerLeave: true,
    });

    /**
     * Region-style 2D heat map widget.
     * Uses the daily periodic dataset with periodicAsPeriodic so the backend returns
     * time-bucketed rows (date dimension on X-axis) grouped by the row dimension (Y-axis).
     *
     * The periodic dataset returns dealership-level OUs (leaf level). To show zone-level rows,
     * the custom dataResolver walks each OU up the hierarchy to find its zone ancestor, then
     * aggregates (sums) dealership-level metrics by zone × date before handing the data to
     * the heatmap transformer.
     */
    const makeHeatMapWidget = ({
        id,
        title,
        rowDimensionId,
        valueMetricId,
        rowAxisLabel,
        columnAxisLabel,
        colorScheme,
        ouTemplateLevelId,
    }: {
        id: Uuid;
        title: string | null;
        rowDimensionId: Uuid;
        valueMetricId: Uuid;
        rowAxisLabel: string;
        columnAxisLabel: string;
        colorScheme?: "sequentialBlue" | "sequentialGreen";
        ouTemplateLevelId: Uuid;
    }): Widget => ({
        id,
        type: WidgetType.heatMap,
        datasetId: emotoradDailyPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [rowDimensionId],
        metrics: [valueMetricId],
        rowDimensionId,
        valueMetricId,
        rowAxisLabel,
        columnAxisLabel,
        colorScheme,
        externalFilters: [],
        editTimeFilters: [],
        viewTimeFilters: [],
        sortConfiguration: [],
        dataResolver: async (dataset, props) => {
            const result = await partialFetchData(dataset, props);
            if (result.success !== true) {
                return result;
            }
            const raw = result.data;
            const ouColumn = raw.columnMetadata.find((c) => c.id === rowDimensionId);
            if (ouColumn === undefined) {
                return dataResult(raw);
            }

            const ouById = new Map<string, OrganizationalUnit>();
            for (const ou of organizationalUnits) {
                ouById.set(ou.id, ou);
            }

            const zoneTemplate = ouTemplates.find((t) => t.id === ouTemplateLevelId);
            const resolveZoneName = (ou: OrganizationalUnit): string => {
                if (zoneTemplate !== undefined) {
                    return processOttopilotStringTemplate(
                        zoneTemplate.instanceDisplayNameTemplate,
                        ou.additionalColumnValues,
                    );
                }
                return ou.id;
            };

            const zoneAncestorCache = new Map<string, string | null>();
            const findZoneDisplayName = (ouDisplayName: string): string | null => {
                if (zoneAncestorCache.has(ouDisplayName)) {
                    return zoneAncestorCache.get(ouDisplayName)!;
                }
                const ou = organizationalUnits.find((o) => {
                    const t = ouTemplates.find((tmpl) => tmpl.id === o.templateId);
                    if (t === undefined) return false;
                    return processOttopilotStringTemplate(t.instanceDisplayNameTemplate, o.additionalColumnValues) === ouDisplayName;
                });
                if (ou === undefined) {
                    zoneAncestorCache.set(ouDisplayName, null);
                    return null;
                }
                if (ou.templateId === ouTemplateLevelId) {
                    const name = resolveZoneName(ou);
                    zoneAncestorCache.set(ouDisplayName, name);
                    return name;
                }
                const visited = new Set<string>();
                let current: OrganizationalUnit | undefined = ou;
                while (current !== undefined && visited.has(current.id) === false) {
                    visited.add(current.id);
                    if (current.templateId === ouTemplateLevelId) {
                        const name = resolveZoneName(current);
                        zoneAncestorCache.set(ouDisplayName, name);
                        return name;
                    }
                    current = current.parentId !== null ? ouById.get(current.parentId) : undefined;
                }
                zoneAncestorCache.set(ouDisplayName, null);
                return null;
            };

            const metricColumn = raw.columnMetadata.find((c) => c.id === valueMetricId);
            if (metricColumn === undefined) {
                return dataResult(raw);
            }
            const timeColumn = raw.columnMetadata.find(
                (c) => c.role === DatasetColumnRole.dimension && c.id !== ouColumn.id,
            );

            type AggKey = string;
            const aggregated = new Map<AggKey, {zone: string; time: string; sum: number}>();

            for (const row of raw.dataPoints) {
                const ouValue = row[ouColumn.columnIndex]?.value;
                if (typeof ouValue !== "string") continue;

                const zoneName = findZoneDisplayName(ouValue);
                if (zoneName === null) continue;

                const rawTimeValue = timeColumn !== undefined ? row[timeColumn.columnIndex]?.value : null;
                const timeCanonical =
                    typeof rawTimeValue === "boolean"
                        ? heatMapCanonicalDayIso(String(rawTimeValue))
                        : heatMapCanonicalDayIso(rawTimeValue as string | number | null);
                if (timeCanonical === "") {
                    continue;
                }
                const metricValue = row[metricColumn.columnIndex]?.value;
                const numericValue = typeof metricValue === "number" ? metricValue : 0;

                const key = `${zoneName}\u0000${timeCanonical}`;
                const existing = aggregated.get(key);
                if (existing !== undefined) {
                    existing.sum += numericValue;
                } else {
                    aggregated.set(key, {zone: zoneName, time: timeCanonical, sum: numericValue});
                }
            }

            const aggregatedDataPoints: Array<Array<{value: string | number | null}>> = Array.from(aggregated.values()).map((entry) => {
                const row: Array<{value: string | number | null}> = [];
                row[ouColumn.columnIndex] = {value: entry.zone};
                if (timeColumn !== undefined) {
                    row[timeColumn.columnIndex] = {value: entry.time};
                }
                row[metricColumn.columnIndex] = {value: entry.sum};
                return row;
            });

            const allZoneRowKeys = Array.from(
                new Set(
                    organizationalUnits
                        .filter((ou) => ou.templateId === ouTemplateLevelId)
                        .map((ou) => resolveZoneName(ou)),
                ),
            ).sort((a, b) => a.localeCompare(b));

            const heatMapFixedColumnKeys = heatMapFixedColumnKeysForCalendarMonthOfRangeEnd(props.to);
            return dataResult({
                ...raw,
                dataPoints: aggregatedDataPoints,
                heatMapFixedRowKeys: allZoneRowKeys,
                ...(heatMapFixedColumnKeys.length > 0 ? {heatMapFixedColumnKeys} : {}),
            });
        },
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsPeriodic,
    });

    /**
     * Distance-bucket bar chart (Dealership Distance Bucket × Journeys).
     *
     * Uses point-in-time-as-non-time-series to match the backend query contract used while
     * validating this widget via API (single categorical dimension + single metric).
     */
    const makeDistanceDistributionWidget = ({
        id,
        title,
    }: {
        id: Uuid;
        title: string | null;
    }): Widget => {
        const baseWidget = makeChartWidget(
            id,
            WidgetType.multiBarGraph,
            title,
            [EMOTORAD.journeys],
            [EMOTORAD.dealershipDistanceBucket],
            AnalyticsQueryVariant.pointInTimeAsNonTimeSeries,
        );
        return {
            ...baseWidget,
            chartColorPalette: [EMOTORAD_DASHBOARD_DISTANCE_BAR_COLOR],
            dataResolver: async (dataset, props) => {
                const result = await partialFetchData(dataset, props);
                if (result.success !== true) {
                    return result;
                }
                const raw = result.data;
                const distanceColumn = raw.columnMetadata.find((column) => column.id === EMOTORAD.dealershipDistanceBucket);
                const metricColumn = raw.columnMetadata.find((column) => column.id === EMOTORAD.journeys);
                if (distanceColumn === undefined || metricColumn === undefined) {
                    return dataResult(raw);
                }

                const rankDistanceBucket = (value: unknown): number => {
                    if (typeof value !== "string") {
                        return Number.POSITIVE_INFINITY;
                    }
                    const trimmed = value.trim();
                    const rangeMatch = /^(\d+)\s*-\s*(\d+)/.exec(trimmed);
                    if (rangeMatch !== null) {
                        return Number(rangeMatch[1]);
                    }
                    const plusMatch = /^(\d+)\s*\+/.exec(trimmed);
                    if (plusMatch !== null) {
                        return Number(plusMatch[1]);
                    }
                    return Number.POSITIVE_INFINITY;
                };

                const sortedDataPoints = [...raw.dataPoints].sort((left, right) => {
                    const leftValue = left[distanceColumn.columnIndex]?.value;
                    const rightValue = right[distanceColumn.columnIndex]?.value;
                    return rankDistanceBucket(leftValue) - rankDistanceBucket(rightValue);
                });

                return dataResult({
                    ...raw,
                    columnMetadata: raw.columnMetadata.map((column) =>
                        column.id === EMOTORAD.journeys
                            ? {...column, displayName: "Leads"}
                            : column
                    ),
                    dataPoints: sortedDataPoints,
                });
            },
        };
    };

    /**
     * Number tile whose value is derived in the frontend rather than fetched as a single backend column.
     *
     * The tile surfaces a synthetic metric id that is never sent to the backend. Its `dataResolver`
     * issues an analytics query for the underlying real columns (via `resolveValue`), runs an
     * arbitrary computation, and returns a single-cell {@link RawData} keyed to the synthetic id so
     * the NumberTile widget renders the result.
     */
    const makeComputedNumberTileWidget = ({
        id,
        title,
        syntheticMetricId,
        valueSuffix,
        decimalPlaces,
        editTimeFilters,
        resolveValue,
    }: {
        id: Uuid;
        title: string;
        syntheticMetricId: Uuid;
        valueSuffix: string;
        decimalPlaces: number;
        editTimeFilters?: Array<Filter>;
        resolveValue: (params: {
            dataset: Dataset;
            props: DataResolverProps;
            fetch: (props: DataResolverProps) => Promise<Result<RawData>>;
        }) => Promise<Result<number>>;
    }): Widget => ({
        id,
        type: WidgetType.numberTile,
        datasetId: emotoradPerformanceDataset.id,
        datasetResolver: partialGetDataset,
        title,
        startDate: startTime,
        endDate: endTime,
        dimensions: [],
        metrics: [syntheticMetricId],
        externalFilters: [],
        editTimeFilters: editTimeFilters ?? [],
        viewTimeFilters: [],
        sortConfiguration: [],
        timeZone,
        hideDateRangeFilter: true,
        hideKebabMenu: true,
        queryVariant: AnalyticsQueryVariant.periodicAsNonTimeSeries,
        numberSystem: NumberSystems.indian,
        decimalPlaces,
        spreadValue: false,
        valuePrefix: "",
        valueSuffix,
        dataResolver: async (dataset, props) => {
            const valueResult = await resolveValue({
                dataset,
                props,
                fetch: (innerProps) => partialFetchData(dataset, innerProps),
            });
            if (valueResult.success !== true) {
                return valueResult;
            }
            const synthetic: RawData = {
                columnMetadata: [{
                    id: syntheticMetricId,
                    columnIndex: 0,
                    displayName: title,
                    dataType: DatasetColumnDataType.number,
                    role: DatasetColumnRole.metric,
                }],
                dataPoints: [[{value: valueResult.data}]],
            };
            return dataResult(synthetic);
        },
    });

    return {
        id: uniqueUuid`74d320ca-05a5-4cdb-afd0-10bf9a1d2c38`,
        title: "Emotorad Performance Dashboard",
        sections: [
            {
                id: uniqueUuid`f5c9a21b-b253-4ce0-8d72-26811c8216cf`,
                title: "Key Performance Snapshot",
                rows: [
                    {
                        title: null,
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTileWidget(
                                uniqueUuid`c6c5ef6c-687d-40ee-9b38-cdf4f0d0e261`,
                                "Leads -> Calls",
                                [EMOTORAD.leadToCallsPercent, EMOTORAD.leads, EMOTORAD.calls],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`cf07dab9-188f-4d77-85af-8461a0b6cc82`,
                                "Calls -> Walk-ins",
                                [EMOTORAD.callsToWalkInsPercent, EMOTORAD.calls, EMOTORAD.walkIns],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`719f04ce-38e7-42d6-826d-1cdf3b5944f6`,
                                "Walk-in -> Testride",
                                [EMOTORAD.walkInsToTestRidesPercent, EMOTORAD.walkIns, EMOTORAD.testRides],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`38f9146c-6aae-46b0-bb5b-45329394180f`,
                                "Testride -> Sale",
                                [EMOTORAD.testRidesToSalesPercent, EMOTORAD.testRides, EMOTORAD.sales],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`c0d27e5c-7063-4db6-a60b-c0799bb1ba90`,
                                "Walk-in -> Sale",
                                [EMOTORAD.walkInsToSalesPercent, EMOTORAD.walkIns, EMOTORAD.sales],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`7d2a45d9-9a0f-4d16-a42e-35e32064273c`,
                                "Lead -> Sale",
                                [EMOTORAD.leadToSalesPercent, EMOTORAD.leads, EMOTORAD.sales],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`79cfb04a-3d95-4f1b-814f-95850bd17837`,
                                "Calls Per Sale",
                                [EMOTORAD.callsPerSale, EMOTORAD.calls, EMOTORAD.sales],
                                "",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`9aa72cb9-3934-4c01-8f71-f44aab13f015`,
                                "Leads per Sale",
                                [EMOTORAD.leadsPerSale, EMOTORAD.leads, EMOTORAD.sales],
                                "",
                                1,
                            ),
                        ],
                    },
                    {
                        title: "Event-wise performance trends",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            /**
                             * Send only the five stage counts (the only periodic-as-periodic
                             * columns that the backend actually returns per day for this dataset).
                             * Each tab's "Conversion %" line is derived on the FE as
                             * `(next stage / current stage) * 100` per row, except the Sales tab
                             * which surfaces the cumulative `Lead -> Sales` ratio.
                             */
                            makeTabbedTimeSeriesLineChartWidget({
                                id: uniqueUuid`a9f3c2d1-8e4b-5a7c-9d2e-1b0f6a3c8d4e`,
                                title: null,
                                metrics: [
                                    EMOTORAD_DAILY.leads,
                                    EMOTORAD_DAILY.calls,
                                    EMOTORAD_DAILY.walkIns,
                                    EMOTORAD_DAILY.testRides,
                                    EMOTORAD_DAILY.sales,
                                ],
                                metricTabs: [
                                    {
                                        tabId: "leads",
                                        label: "Leads",
                                        primaryMetricId: EMOTORAD_DAILY.leads,
                                        seriesColors: EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS.leads,
                                        derivedSecondary: {
                                            numeratorMetricId: EMOTORAD_DAILY.calls,
                                            denominatorMetricId: EMOTORAD_DAILY.leads,
                                            multiplyBy: 100,
                                            label: "Conversion %",
                                        },
                                    },
                                    {
                                        tabId: "calls",
                                        label: "Calls",
                                        primaryMetricId: EMOTORAD_DAILY.calls,
                                        seriesColors: EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS.calls,
                                        derivedSecondary: {
                                            numeratorMetricId: EMOTORAD_DAILY.walkIns,
                                            denominatorMetricId: EMOTORAD_DAILY.calls,
                                            multiplyBy: 100,
                                            label: "Conversion %",
                                        },
                                    },
                                    {
                                        tabId: "walk-ins",
                                        label: "Walk-ins",
                                        primaryMetricId: EMOTORAD_DAILY.walkIns,
                                        seriesColors: EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS["walk-ins"],
                                        derivedSecondary: {
                                            numeratorMetricId: EMOTORAD_DAILY.testRides,
                                            denominatorMetricId: EMOTORAD_DAILY.walkIns,
                                            multiplyBy: 100,
                                            label: "Conversion %",
                                        },
                                    },
                                    {
                                        tabId: "test-ride",
                                        label: "Test Ride",
                                        primaryMetricId: EMOTORAD_DAILY.testRides,
                                        seriesColors: EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS["test-ride"],
                                        derivedSecondary: {
                                            numeratorMetricId: EMOTORAD_DAILY.sales,
                                            denominatorMetricId: EMOTORAD_DAILY.testRides,
                                            multiplyBy: 100,
                                            label: "Conversion %",
                                        },
                                    },
                                    {
                                        tabId: "sales",
                                        label: "Sales",
                                        primaryMetricId: EMOTORAD_DAILY.sales,
                                        seriesColors: EMOTORAD_DASHBOARD_TABBED_LINE_CHART_SERIES_COLORS.sales,
                                        derivedSecondary: {
                                            numeratorMetricId: EMOTORAD_DAILY.sales,
                                            denominatorMetricId: EMOTORAD_DAILY.leads,
                                            multiplyBy: 100,
                                            label: "Conversion %",
                                        },
                                    },
                                ],
                            }),
                        ],
                    },
                    {
                        title: "KAM-wise Dealer Performance Report",
                        type: DashboardRowType.tableRow,
                        widgets: [
                            makeLeaderTableWidget({
                                id: uniqueUuid`a47b1c92-3d6e-4f80-a1c2-7d9e5b0c6f12`,
                                /** Title lives on the row (rendered by the dashboard); widget header would duplicate it. */
                                title: null,
                                metrics: [
                                    EMOTORAD.leads,
                                    EMOTORAD.calls,
                                    EMOTORAD.testRides,
                                    EMOTORAD.walkIns,
                                    EMOTORAD.sales,
                                ],
                                metricColumnLabels: [
                                    "Leads",
                                    "Calls",
                                    "Testride",
                                    "Walk-Ins",
                                    "Sales",
                                ],
                                /**
                                 * Tree levels rendered as columns. Backend returns one row per SSE
                                 * (filtered below), and the FE walks `parentId` to populate the
                                 * matching ancestor at each level. Order is top-of-tree → leaf.
                                 */
                                roleChainTopDown: [
                                    {templateId: EmotoradRoles.KAM as Uuid, columnLabel: "KAM Name"},
                                    {
                                        templateId: EmotoradRoles.DealershipOwner as Uuid,
                                        columnLabel: "Dealership Name",
                                        pathDisplay: "dealership",
                                    },
                                    {templateId: EmotoradRoles.StoreSalesExecutive as Uuid, columnLabel: "SSE Name"},
                                ],
                                leafTemplateIdsToInclude: [EmotoradRoles.StoreSalesExecutive as Uuid],
                            }),
                        ],
                    },
                    {
                        title: "RM-wise Dealer Performance Report",
                        type: DashboardRowType.tableRow,
                        widgets: [
                            makeLeaderTableWidget({
                                id: uniqueUuid`c58d2ea3-4f7f-5b91-b2d3-8e0a6c1d9e23`,
                                title: null,
                                metrics: [
                                    EMOTORAD.leads,
                                    EMOTORAD.calls,
                                    EMOTORAD.testRides,
                                    EMOTORAD.walkIns,
                                    EMOTORAD.sales,
                                ],
                                metricColumnLabels: [
                                    "Leads",
                                    "Calls",
                                    "Testride",
                                    "Walk-Ins",
                                    "Sales",
                                ],
                                roleChainTopDown: [
                                    {templateId: EmotoradRoles.RM as Uuid, columnLabel: "RM Name"},
                                    {
                                        templateId: EmotoradRoles.DealershipOwner as Uuid,
                                        columnLabel: "Dealership Name",
                                        pathDisplay: "dealership",
                                    },
                                    {templateId: EmotoradRoles.StoreSalesExecutive as Uuid, columnLabel: "SSE Name"},
                                ],
                                leafTemplateIdsToInclude: [EmotoradRoles.StoreSalesExecutive as Uuid],
                            }),
                        ],
                    },
                    {
                        title: "Drop-off & Conversion Funnel Tracker",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeFunnelWidget({
                                id: uniqueUuid`b8e1c2d4-9f06-4a72-9b30-5d2f8a4c7e11`,
                                title: null,
                                /**
                                 * Order matters — top-of-funnel first. Conversion% is computed against
                                 * the first stage (Leads); drop-off% is computed vs the previous stage.
                                 */
                                metrics: [
                                    EMOTORAD.leads,
                                    EMOTORAD.calls,
                                    EMOTORAD.walkIns,
                                    EMOTORAD.testRides,
                                    EMOTORAD.sales,
                                ],
                            }),
                        ],
                    },
                    {
                        title: "Disposition Analysis View",
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            {
                                ...makeChartWidget(
                                    uniqueUuid`a3d4f127-6c39-4f5c-8e1d-2c6a917b0e44`,
                                    WidgetType.donutChart,
                                    null,
                                    [EMOTORAD.leads],
                                    [EMOTORAD.disposition],
                                    AnalyticsQueryVariant.periodicAsNonTimeSeries,
                                    {
                                        dismissChartHoverTooltipOnPointerLeave: true,
                                        chartColorPalette: EMOTORAD_DASHBOARD_DONUT_SLICE_COLORS,
                                    },
                                ),
                            },
                        ],
                    },
                    {
                        title: "Alerts & Exception Monitoring",
                        type: DashboardRowType.numberTilesRow,
                        widgets: [
                            makeNumberTileWidget(
                                uniqueUuid`6c2a51f0-7c63-4f9c-8e6e-1a4b21d3e7c9`,
                                "Leads Not Contacted %",
                                [EMOTORAD.leadToCallsDropOffPercent, EMOTORAD.leads, EMOTORAD.calls],
                                "%",
                                1,
                            ),
                            makeNumberTileWidget(
                                uniqueUuid`9f3b6e21-1d4f-4f02-92ad-3b7c1e5d2a90`,
                                "Conversion drop",
                                [EMOTORAD.leadToSalesDropOffPercent, EMOTORAD.leads, EMOTORAD.sales],
                                "%",
                                1,
                            ),
                            makeComputedNumberTileWidget({
                                id: uniqueUuid`d05f49a1-6c8b-4d03-bf5e-2a9d3c1f7b40`,
                                title: "Zero-Sales High Leads %",
                                syntheticMetricId: EMOTORAD__COMPUTED__ZERO_SALES_HIGH_LEADS_PERCENT__COLUMN_ID,
                                valueSuffix: "%",
                                decimalPlaces: 1,
                                resolveValue: async ({props, fetch}) => {
                                    const queryResult = await fetch({
                                        ...props,
                                        metrics: [
                                            {id: EMOTORAD.leads, aggregationFunction: MetricAggregationFunction.sum},
                                            {id: EMOTORAD.sales, aggregationFunction: MetricAggregationFunction.sum},
                                        ],
                                        dimensions: [],
                                    } as DataResolverProps);
                                    if (queryResult.success !== true) {
                                        return queryResult;
                                    }
                                    const data = queryResult.data;
                                    const leadsCol = data.columnMetadata.find((c) => c.id === EMOTORAD.leads);
                                    const salesCol = data.columnMetadata.find((c) => c.id === EMOTORAD.sales);
                                    if (leadsCol === undefined || salesCol === undefined) {
                                        return errorResult(new Error("Zero-Sales High Leads %: leads or sales column missing in response"));
                                    }
                                    const row = data.dataPoints[0];
                                    if (row === undefined) {
                                        return dataResult(0);
                                    }
                                    const leads = Number(row[leadsCol.columnIndex]?.value ?? 0);
                                    const sales = Number(row[salesCol.columnIndex]?.value ?? 0);
                                    if (leads <= 0) {
                                        return dataResult(0);
                                    }
                                    return dataResult(((leads - sales) / leads) * 100);
                                },
                            }),
                            makeComputedNumberTileWidget({
                                id: uniqueUuid`e72cd5b3-0a47-4f1e-9d3b-8c0e6d2a4f51`,
                                title: "No Call Activity (SSE)",
                                syntheticMetricId: EMOTORAD__COMPUTED__NO_CALL_ACTIVITY_SSE__COLUMN_ID,
                                valueSuffix: "",
                                decimalPlaces: 0,
                                resolveValue: async ({props, fetch}) => {
                                    const queryResult = await fetch({
                                        ...props,
                                        metrics: [
                                            {id: EMOTORAD.calls, aggregationFunction: MetricAggregationFunction.sum},
                                        ],
                                        dimensions: [
                                            {id: EMOTORAD.leadOwner},
                                            {id: EMOTORAD.user},
                                        ],
                                        filters: {
                                            type: FilterType.stringIn,
                                            attribute: EMOTORAD.userTemplate,
                                            values: [EmotoradRoles.StoreSalesExecutive],
                                        },
                                    } as DataResolverProps);
                                    if (queryResult.success !== true) {
                                        console.error("[No Call Activity (SSE)] fetch failed:", queryResult.error);
                                        return queryResult;
                                    }
                                    const data = queryResult.data;
                                    const callsCol = data.columnMetadata.find((c) => c.id === EMOTORAD.calls);
                                    if (callsCol === undefined) {
                                        return errorResult(new Error("No Call Activity (SSE): calls column missing in response"));
                                    }
                                    const callsIndex = callsCol.columnIndex;
                                    const zeroCount = data.dataPoints.reduce((acc, dataRow) => {
                                        const cell = dataRow[callsIndex];
                                        const callsValue = cell?.value;
                                        const isZero = callsValue === 0
                                            || callsValue === null
                                            || callsValue === undefined
                                            || callsValue === "0";
                                        return isZero ? acc + 1 : acc;
                                    }, 0);
                                    return dataResult(zeroCount);
                                },
                            }),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeDistanceDistributionWidget({
                                id: uniqueUuid`0f76f12e-f4b0-4a4f-bf8d-5f6d17d9a001`,
                                title: "Distance-based Lead Distribution",
                            }),
                        ],
                    },
                    {
                        title: null,
                        type: DashboardRowType.chartsRow,
                        widgets: [
                            makeHeatMapWidget({
                                id: uniqueUuid`5a6b7c8d-9e01-4f23-a456-b7890c1d2e3f`,
                                title: "Conversion Volume Heatmap",
                                rowDimensionId: EMOTORAD_DAILY.ou,
                                valueMetricId: EMOTORAD_DAILY.sales,
                                rowAxisLabel: "Zones",
                                columnAxisLabel: "Date",
                                colorScheme: "sequentialBlue",
                                ouTemplateLevelId: EMOTORAD__HEATMAP__ZONE_OU_TEMPLATE_ID,
                            }),
                        ],
                    },
                ],
            },
        ],
    };
}
